import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  capitalize,
  cn,
  controlOptions,
  createState,
  flaggedControls,
  getControlName,
  isDataControl,
  isDefensiveControl,
  isDiscoveredDevicesControl,
  isEmailControl,
  isEndpointManagementControl,
  isIdentityProviderControl,
  isUserMatch,
  isVulnerabilityManagementControl,
  pluralize,
  queries,
  service,
  useAccountQuery,
  useAuthConfig,
  useHasControl,
  ValidControlCode,
} from "@theprelude/core";
import {
  Button,
  CheckmarkIcon,
  CloseIcon,
  DatabaseIcon,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  InputFormField,
  MailIcon,
  NetworkIcon,
  notifyError,
  notifySuccess,
  ScanIcon,
  ServerIcon,
  SettingsIcon,
  ShieldIcon,
  ShieldUser,
  UserAddIcon,
} from "@theprelude/ds";
import { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { setPageTitle } from "@/components/app-shell/app-shell";
import EditProfileButton from "@/components/edit-profile-button";
import { hasFeatures } from "@/components/flag/flag";
import { useOverlay } from "@/hooks/use-overlay";
import { usePopper } from "@/hooks/use-popper";
import { tb } from "@/lib/tracking";

export const Route = createFileRoute("/_app/account/")({
  beforeLoad() {
    setPageTitle("Account");
  },
  component: SettingsPage,
});

export const useUpdateAccountModal = createState<boolean>(false);

function SettingsPage() {
  const authConfig = useAuthConfig();
  const { data: account } = useSuspenseQuery(
    queries.iam.getAccount(authConfig),
  );
  const name = account.users.find((u) => isUserMatch(account.whoami, u))?.name;
  const userCount = account.users.length;
  const overlay = useOverlay();

  return (
    <div className="flex flex-col gap-4 p-6">
      <SettingsCard title="Account Settings">
        <div className="mt-2 flex flex-col">
          <div className="text-rg mb-2 leading-6 text-secondary-foreground">
            Company
          </div>
          <div className="mb-4">{account.company}</div>
          <UpdateAccountModal key={account.company} />
        </div>
      </SettingsCard>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <SettingsCard title="My Profile">
          <div className="mt-2 flex flex-col">
            {name && (
              <>
                <div className="text-rg mb-2 leading-6 text-secondary-foreground">
                  Name
                </div>
                <div className="mb-4">{name}</div>
              </>
            )}
            <div className="text-rg mb-2 leading-6 text-secondary-foreground">
              Email
            </div>
            <div className="mb-4">{account.whoami.handle}</div>
            <div className="flex gap-4">
              <EditProfileButton />
            </div>
          </div>
        </SettingsCard>

        <SettingsCard title="Account Users">
          <div className="mt-2 flex flex-col">
            <div className="text-rg mb-4 leading-6">{`${userCount} ${pluralize(
              userCount ?? 0,
              "user",
            )}`}</div>
            <div className="flex gap-4">
              <Link to={"/account/users"}>
                <Button
                  icon={<UserAddIcon className="text-secondary-foreground" />}
                  intent="secondary"
                  className={tb("manage-users")}
                >
                  Manage users
                </Button>
              </Link>
              <Button
                onClick={() => {
                  void overlay.open("invite-user");
                }}
                icon={<UserAddIcon />}
                intent="primary"
                className={tb("invite-a-user")}
              >
                Invite a user
              </Button>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard title="Integrations">
          <div className="flex flex-col">
            <IntegrationsGroup
              label="Device Discoverability"
              icon={<ScanIcon className="text-secondary-foreground" />}
              filter={isDiscoveredDevicesControl}
            />
            <IntegrationsGroup
              label="Defensive"
              icon={<ShieldIcon className="text-secondary-foreground" />}
              filter={isDefensiveControl}
            />
            <IntegrationsGroup
              label="Endpoint Management"
              icon={<ServerIcon className="text-secondary-foreground" />}
              filter={isEndpointManagementControl}
            />
            <IntegrationsGroup
              label="Vulnerability Management"
              icon={<ServerIcon className="text-secondary-foreground" />}
              filter={isVulnerabilityManagementControl}
            />
            <IntegrationsGroup
              label="Identity Provider"
              icon={<ShieldUser className="text-secondary-foreground" />}
              filter={isIdentityProviderControl}
            />
            <IntegrationsGroup
              label="Email"
              icon={<MailIcon className="text-secondary-foreground" />}
              filter={isEmailControl}
            />
            <IntegrationsGroup
              label="Data"
              icon={<DatabaseIcon className="text-secondary-foreground" />}
              filter={isDataControl}
            />
            <Link to={"/account/integrations"}>
              <Button
                className={cn("mt-4", tb("manage-integrations"))}
                icon={<NetworkIcon className="text-secondary-foreground" />}
                intent="secondary"
              >
                Manage integrations
              </Button>
            </Link>
          </div>
        </SettingsCard>

        <SettingsCard title="OpenID Connect Settings">
          <div className="mt-2 flex flex-col">
            <div className="mb-4 flex items-center justify-between">
              {account.oidc?.issuer ? (
                <>
                  <div>{capitalize(account.oidc?.issuer)}</div>
                  <span className="flex items-center gap-1 whitespace-nowrap text-secondary-foreground">
                    <CheckmarkIcon className="h-4 w-4 shrink-0 fill-status-green" />
                    Configured
                  </span>
                </>
              ) : (
                <div className="leading-6 text-secondary-foreground">
                  No OIDC configured
                </div>
              )}
            </div>
            <Link to={"/account/oidc"}>
              <Button
                icon={<SettingsIcon className="text-secondary-foreground" />}
                intent="secondary"
                className={tb("manage-oidc-settings")}
              >
                Manage settings
              </Button>
            </Link>
          </div>
        </SettingsCard>
      </div>
    </div>
  );
}

const SettingsCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  return (
    <div className="border border-stroke-line bg-surface-background p-3">
      <div className={"flex items-center justify-between pb-2"}>
        <span className="text-lg dark:font-light">{title}</span>
      </div>
      <div className="border-b border-stroke-line" />
      {children}
    </div>
  );
};

const IntegrationsGroup = ({
  label,
  icon,
  filter,
}: {
  label: string;
  icon: ReactNode;
  filter: (control: ValidControlCode) => boolean;
}) => {
  const authConfig = useAuthConfig();
  const { data: account } = useSuspenseQuery(
    queries.iam.getAccount(authConfig),
  );

  const integrations = controlOptions
    .filter((control) => filter(control.id))
    .filter((control) => {
      const flag = flaggedControls.find((f) => f.control === control.id)?.flag;
      if (!flag) return true;

      return hasFeatures(account, [flag]);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  if (integrations.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 border-b border-stroke-line py-4 text-base text-primary-foreground font-medium dark:font-light">
        {icon}
        {label} Integrations
      </div>
      {integrations.map((c) => (
        <div
          key={`account-integration-${c.id}`}
          className="flex items-center justify-between border-b border-dashed border-stroke-line py-2 last:border-none"
        >
          <Integration control={c.id} />
        </div>
      ))}
    </div>
  );
};

const Integration: React.FC<{ control: ValidControlCode }> = ({ control }) => {
  const { data: connected } = useHasControl(control);
  return (
    <>
      <span>{getControlName(control)}</span>
      {connected ? (
        <span className="flex items-center gap-1 whitespace-nowrap text-status-green">
          <CheckmarkIcon className="h-4 w-4 shrink-0" /> Connected
        </span>
      ) : (
        <span className="flex items-center gap-1 whitespace-nowrap text-secondary-foreground">
          <CloseIcon className="h-4 w-4 shrink-0" /> Not Connected
        </span>
      )}
    </>
  );
};

type UpdateAccountForm = {
  company: string;
};

export const UpdateAccountModal = () => {
  const dialog = usePopper();
  const authConfig = useAuthConfig();
  const { data: account } = useAccountQuery();

  const form = useForm<UpdateAccountForm>({
    defaultValues: {
      company: account?.company,
    },
  });

  const { formState, register } = form;

  const updateAccount = useMutation({
    mutationFn: async ({ company }: UpdateAccountForm) => {
      return await service(authConfig).iam.updateAccount({ company });
    },
  });

  const submit = (data: UpdateAccountForm) => {
    updateAccount.mutate(
      {
        ...data,
      },
      {
        onSuccess: async () => {
          notifySuccess({
            message: "Successfully updated account",
            dismissible: true,
          });
          dialog.close();
        },
        onError: async (error) => {
          notifyError({
            message: "Error occurred while trying to update account",
            error,
            dismissible: true,
          });
        },
      },
    );
  };

  return (
    <Dialog {...dialog}>
      <DialogTrigger asChild>
        <Button intent="secondary" className={tb("edit-company-name")}>
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <Form {...form} onSubmitValid={submit}>
          <DialogHeader>
            <DialogTitle>
              {account?.company ? `Rename ${account.company}` : "Add company"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <InputFormField
              {...register("company")}
              label="Company"
              placeholder="Enter name of affiliated company"
              required
            />
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button
              loading={updateAccount.isPending}
              disabled={!formState.isDirty || updateAccount.isPending}
              iconPosition="right"
              intent="primary"
              type="submit"
            >
              Save
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
